import React, { useRef, Children, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { SmartphoneIcon, ShieldCheckIcon, GlobeIcon } from 'lucide-react';
export function FeaturesDMRV() {
  const ref = useRef(null);
  const [activeCard, setActiveCard] = useState(1);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 40
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  return <section ref={ref} className="w-full bg-white py-20 sm:py-28" data-id="element-994">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-995">
        <motion.div className="text-center mb-14" initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-996">

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-carbon-deep tracking-tight" data-id="element-997">
            From field to boardroom
          </h2>
          <p className="mt-4 text-lg text-carbon-deep max-w-2xl mx-auto" data-id="element-998">
            Two platforms, one mission: audit-grade emissions data and
            compliance-ready reporting trusted by regulators, buyers, and
            investors.
          </p>
        </motion.div>

        <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8" initial="hidden" animate={isInView ? 'visible' : 'hidden'} variants={containerVariants} data-id="element-999">

          <motion.article className="relative overflow-hidden p-8 rounded-2xl border border-[#9aa6af] bg-white cursor-pointer" variants={cardVariants} whileHover={{
          y: -6,
          boxShadow: '0 12px 32px -8px rgba(0, 0, 0, 0.12)',
          borderColor: 'rgba(34, 197, 94, 0.3)',
          transition: {
            duration: 0.25,
            ease: 'easeOut'
          }
        }} animate={activeCard === 0 ? {
          y: -6,
          boxShadow: '0 12px 32px -8px rgba(0, 0, 0, 0.12)',
          borderColor: 'rgba(34, 197, 94, 0.3)'
        } : {
          y: 0,
          boxShadow: '0 0 0 0 rgba(0, 0, 0, 0)',
          borderColor: '#9aa6af'
        }} onMouseEnter={() => setActiveCard(0)} onFocus={() => setActiveCard(0)} data-id="element-1000">

            <div className="absolute -top-10 -right-10 h-28 w-28 rounded-3xl bg-[#8dd95b]/15" aria-hidden="true" data-id="element-1001" />
            <div className="w-12 h-12 rounded-xl bg-carbon-bright/10 flex items-center justify-center mb-6" data-id="element-1002">
              <SmartphoneIcon className="w-6 h-6 text-carbon-bright" data-id="element-1003" />
            </div>
            <h3 className="text-xl font-bold text-carbon-deep mb-3" data-id="element-1004">
              Mobile-First Field dMRV
            </h3>
            <p className="text-carbon-deep leading-relaxed" data-id="element-1005">
              Farmers and field agents log harvest, cookstove, and land-use data
              via USSD on any phone — no internet required. GPS-tagged entries
              generate audit-grade emissions records at the source.
            </p>
          </motion.article>

          <motion.article className="relative overflow-hidden p-8 rounded-2xl border border-[#9aa6af] bg-white cursor-pointer" variants={cardVariants} whileHover={{
          y: -6,
          boxShadow: '0 12px 32px -8px rgba(0, 0, 0, 0.12)',
          borderColor: 'rgba(34, 197, 94, 0.3)',
          transition: {
            duration: 0.25,
            ease: 'easeOut'
          }
        }} animate={activeCard === 1 ? {
          y: -6,
          boxShadow: '0 12px 32px -8px rgba(0, 0, 0, 0.12)',
          borderColor: 'rgba(34, 197, 94, 0.3)'
        } : {
          y: 0,
          boxShadow: '0 0 0 0 rgba(0, 0, 0, 0)',
          borderColor: '#9aa6af'
        }} onMouseEnter={() => setActiveCard(1)} onFocus={() => setActiveCard(1)} data-id="element-1006">

            <div className="absolute -top-12 -right-12 h-32 w-32 rounded-full bg-[#8dd95b]/15" aria-hidden="true" data-id="element-1007" />
            <div className="w-12 h-12 rounded-xl bg-carbon-bright/10 flex items-center justify-center mb-6" data-id="element-1008">
              <ShieldCheckIcon className="w-6 h-6 text-carbon-bright" data-id="element-1009" />
            </div>
            <h3 className="text-xl font-bold text-carbon-deep mb-3" data-id="element-1010">
              Enterprise Carbon Accounting
            </h3>
            <p className="text-carbon-deep leading-relaxed" data-id="element-1011">
              Automate Scope 1, 2, and 3 GHG inventories for large enterprises.
              Generate IFRS S1/S2, CSRD, and CBAM-compliant reports with a full
              digital audit trail ready for third-party verification.
            </p>
          </motion.article>

          <motion.article className="relative overflow-hidden p-8 rounded-2xl border border-[#9aa6af] bg-white cursor-pointer" variants={cardVariants} whileHover={{
          y: -6,
          boxShadow: '0 12px 32px -8px rgba(0, 0, 0, 0.12)',
          borderColor: 'rgba(34, 197, 94, 0.3)',
          transition: {
            duration: 0.25,
            ease: 'easeOut'
          }
        }} animate={activeCard === 2 ? {
          y: -6,
          boxShadow: '0 12px 32px -8px rgba(0, 0, 0, 0.12)',
          borderColor: 'rgba(34, 197, 94, 0.3)'
        } : {
          y: 0,
          boxShadow: '0 0 0 0 rgba(0, 0, 0, 0)',
          borderColor: '#9aa6af'
        }} onMouseEnter={() => setActiveCard(2)} onFocus={() => setActiveCard(2)} data-id="element-1012">

            <div className="absolute -top-10 -right-10 h-28 w-28 rounded-2xl rotate-12 bg-[#8dd95b]/15" aria-hidden="true" data-id="element-1013" />
            <div className="w-12 h-12 rounded-xl bg-carbon-bright/10 flex items-center justify-center mb-6" data-id="element-1014">
              <GlobeIcon className="w-6 h-6 text-carbon-bright" data-id="element-1015" />
            </div>
            <h3 className="text-xl font-bold text-carbon-deep mb-3" data-id="element-1016">
              Global Market Access
            </h3>
            <p className="text-carbon-deep leading-relaxed" data-id="element-1017">
              Automated EUDR due diligence, Digital Quality Certificates, and
              ESG disclosures. One platform bridges Africa's producers and
              enterprises with EU and international compliance requirements.
            </p>
          </motion.article>
        </motion.div>
      </div>
    </section>;
}